import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../services/realApi';
import { setToken } from '../utils/auth';
import UserValidationStep from '../components/UserValidationStep';
import CustomerAgreement from '../components/CustomerAgreement';
import {
  FiCheckCircle,
  FiBarChart2,
  FiShield,
  FiArrowRight,
  FiTarget,
  FiCpu,
  FiDatabase,
  FiDollarSign,
  FiTrendingUp,
  FiCheck,
} from 'react-icons/fi';
import { IconType } from 'react-icons';

type SignupStep = 'info' | 'agreement' | 'validation' | 'password';

interface Feature {
  icon: IconType;
  title: string;
  subtitle?: string;
  bullets: string[];
}

interface ValidationResponse {
  is_same_user: boolean;
  success: boolean;
  [key: string]: unknown;
}

interface TrialUpgradeResponse {
  password_setup_token?: string;
  new_user_token?: string;
}

const FEATURES: Feature[] = [
  {
    icon: FiBarChart2,
    title: 'AI Strategy Assessment',
    subtitle: '(Available Now)',
    bullets: [
      'Take our comprehensive AI maturity evaluation',
      'Get a personalized transformation roadmap',
      'Receive AWS service recommendations tailored to your goals',
      'Download a detailed PDF report with prioritized action items',
    ],
  },
  {
    icon: FiDollarSign,
    title: 'Cloud Financial Optimization',
    subtitle: '(FinOps)',
    bullets: [
      'See exactly where your AWS dollars go with real-time cost tracking',
      'Get AI-powered recommendations to cut waste and optimize spending',
      'Detect cost anomalies before they become budget disasters',
      'Track per-project infrastructure costs with resource tagging',
    ],
  },
  {
    icon: FiCpu,
    title: 'GenAI & LLM Lifecycle',
    subtitle: 'Management',
    bullets: [
      'Monitor every LLM call: prompts, completions, costs, and performance',
      'Manage prompts like code: version control, testing, production promotion',
      'Integrate with AWS Bedrock for seamless prompt synchronization',
      'Track agent behavior and ensure safety compliance',
      'Store and manage vector embeddings at scale',
    ],
  },
  {
    icon: FiShield,
    title: 'Cloud Security & Compliance',
    subtitle: 'at Scale',
    bullets: [
      'Automated guardrails enforce policies across all accounts',
      'Tag compliance scoring identifies governance gaps',
      'Security posture assessment highlights IAM, encryption, and logging issues',
      'Violation tracking with audit trails',
    ],
  },
  {
    icon: FiDatabase,
    title: 'Data Pipeline Observability',
    bullets: [
      'Catalog your data assets with automatic lineage discovery',
      'Monitor pipeline quality and job execution health',
      'Track column-level transformations across your data estate',
      'Governance and compliance tracking for data teams',
    ],
  },
  {
    icon: FiTrendingUp,
    title: 'Your Transformation Journey,',
    subtitle: 'Tracked',
    bullets: [
      'Manage AI/cloud transformation projects with milestones and timelines',
      'Budget vs. actual spend visibility',
      'Team collaboration with role-based access',
      'Jira integration to sync with your existing workflows',
      'Complete audit trail of your progress',
    ],
  },
];

const TrialInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshAuth } = useAuth();

  const [signupStep, setSignupStep] = useState<SignupStep>('info');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [validationLoading, setValidationLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [, setValidationResult] = useState<ValidationResponse | null>(null);

  useEffect(() => {
    if (sessionStorage.getItem('trial_needs_password') === 'true' && user) {
      sessionStorage.removeItem('trial_needs_password');
      setSignupStep('password');
    }
  }, [user]);

  // Guard: only assessment_only users can upgrade to trial. Anyone on a
  // higher plan (trial, paid, etc.) lands here from a stale link or by
  // mistake — without this guard they'd reach validation, click through,
  // and hit "Upgrade not available for trial plan" from the backend.
  useEffect(() => {
    if (!user) return;
    if (user.subscription_plan && user.subscription_plan !== 'assessment_only') {
      navigate('/hub/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleStartSignup = () => {
    setSignupStep('agreement');
    setHasScrolledToBottom(false);
  };

  const activateTrialAndProceed = async (
    validationResponse: ValidationResponse,
    email: string,
    name: string,
  ) => {
    try {
      const payload = {
        is_same_user: validationResponse.is_same_user,
        account_owner_email: validationResponse.is_same_user ? undefined : email,
        account_owner_name: validationResponse.is_same_user ? undefined : name,
      };

      const response = await apiCall('/clients/upgrade-to-trial', {
        method: 'POST',
        body: JSON.stringify(payload),
      }) as TrialUpgradeResponse;

      if (response.password_setup_token) {
        sessionStorage.setItem('password_setup_token', response.password_setup_token);
      }

      if (response.new_user_token) {
        setToken(response.new_user_token);
        sessionStorage.setItem('trial_needs_password', 'true');
        await refreshAuth();
        setSignupStep('password');
      } else {
        setSignupStep('password');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to activate trial. Please try again.';
      setValidationError(message);
      throw err;
    }
  };

  const handleValidateUser = async (email: string, name: string): Promise<ValidationResponse> => {
    setValidationLoading(true);
    setValidationError('');

    try {
      const response = await apiCall('/auth/validate-trial-user', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }) as ValidationResponse;

      setValidationResult(response);
      await activateTrialAndProceed(response, email, name);
      return response;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to validate email. Please try again.';
      setValidationError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setValidationLoading(false);
    }
  };

  const handleAgreeToTerms = () => {
    setSignupStep('validation');
  };

  const handlePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const passwordToken = sessionStorage.getItem('password_setup_token');
      if (!passwordToken) {
        throw new Error('No password setup token found');
      }

      await apiCall('/auth/set-password', {
        method: 'POST',
        body: JSON.stringify({ token: passwordToken, password }),
      });

      sessionStorage.removeItem('password_setup_token');
      await refreshAuth();
      navigate('/connect-aws');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to complete signup. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (signupStep === 'agreement') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl w-full space-y-8">
          <div>
            <h1 className="text-center text-3xl font-bold text-gray-900 dark:text-white">
              Aivilo Lens Customer Agreement
            </h1>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              Effective Date: Upon Customer&apos;s acceptance
            </p>
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              Version: 1.4 | Last Updated: January 29, 2026
            </p>
          </div>

          <CustomerAgreement onScrolledToBottom={() => {
            if (!hasScrolledToBottom) setHasScrolledToBottom(true);
          }} />

          <div className="flex flex-col items-center space-y-4">
            {!hasScrolledToBottom && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Please read through the agreement to continue
              </p>
            )}

            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4 max-w-md">
                <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
              </div>
            )}

            <button
              onClick={handleAgreeToTerms}
              disabled={!hasScrolledToBottom || loading}
              className="px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Processing...' : 'I Agree'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (signupStep === 'validation') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors py-12 px-4">
        <div className="max-w-lg w-full">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 transition-colors">
            <UserValidationStep
              currentUserEmail={user?.email ?? ''}
              currentUserName={user?.name ?? ''}
              onValidate={handleValidateUser}
              loading={validationLoading}
              error={validationError}
            />

            {error && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (signupStep === 'password') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Set Your Password
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Create a secure password to activate your 60-day trial
              </p>
            </div>

            <form
              className="space-y-6 bg-white dark:bg-gray-800 rounded-lg p-6 sm:p-8 shadow-sm border border-gray-200 dark:border-gray-700"
              onSubmit={handlePasswordSubmit}
            >
              <div className="space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your new password"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Must be at least 8 characters long
                  </p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                    className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Confirm your new password"
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
                  <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !password || !confirmPassword}
                className="w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Setting Password...' : 'Set Password, Activate 60-Day Trial'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Product info view (default)
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Welcome to Aivilo Lens
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Your AI-powered platform for cloud operations, data management, and AI/ML lifecycle optimization.
          </p>
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 text-center">
            What You&apos;ll Get Access To
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col"
                >
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-base text-gray-900 dark:text-white leading-tight">
                        {feature.title}
                        {feature.subtitle && (
                          <>
                            <br />
                            <span>{feature.subtitle}</span>
                          </>
                        )}
                      </h3>
                    </div>
                  </div>
                  <div className="space-y-2.5 pl-1">
                    {feature.bullets.map((bullet) => (
                      <div key={bullet} className="flex items-start gap-2.5">
                        <FiCheck className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                          {bullet}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <FiTarget className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Start Your 60-Day Trial
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Unlock the full platform: monitor cloud infrastructure, optimize AWS costs, govern AI/ML workloads,
                and manage data pipelines—all from one unified dashboard.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 italic">
                Note: Complementary access to Aivilo Lens is granted during any engineering engagement.
              </p>
              <button
                onClick={handleStartSignup}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Sign Me Up
                <FiArrowRight className="ml-2 w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mb-8">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <FiCheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                Currently Available
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                You have full access to the AI Strategy Assessment feature. View your results or start a new assessment.
              </p>
              <Link
                to="/hub/assessments"
                className="inline-flex items-center mt-3 text-green-600 dark:text-green-400 hover:underline"
              >
                Go to Assessments
                <FiArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        <div className="text-center text-gray-600 dark:text-gray-400">
          <p>
            Need help setting up? Contact us at{' '}
            <a href="mailto:support@aivilo.ai" className="text-blue-600 dark:text-blue-400 hover:underline">
              support@aivilo.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrialInfoPage;
